import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/store';
import { doGetClaims, doUpdateClaim } from '../../../modules/claims/stores/thunk';
import { selectAllClaims } from '../../../modules/claims/stores/slice';
import { Badge } from '../../../components/ui/badge';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../components/ui/pagination';
import { ROLES } from '../../../constants';
import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from '../../../components/ui/menubar';

const Claims = () => {
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const claims = useSelector((state: any) => selectAllClaims(state));
  const metadata = useSelector((state: any) => state.claim.metadata);
  const userRole = useSelector((state: any) => state.auth.user.role);

  const [currentPage, setCurrentPage] = useState(1);

  const fetchClaims = useCallback(
    async (status?: string, page?: number) => {
      await dispatch(
        doGetClaims({
          page,
          take: 10,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const fetchInitialData = async () => {
      await fetchClaims();
    };

    fetchInitialData();
  }, [fetchClaims]);

  const handleClaimStatusChange = async (id: number, status: string) => {
    await dispatch(doUpdateClaim({ id, status }));
  };

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle>Claims</CardTitle>
        <CardDescription>Your most recently updated claims</CardDescription>
      </CardHeader>
      <CardContent>
        {claims.length === 0 ? (
          <p className="text-center text-gray-500">No claims</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Order ID</TableHead>
                  <TableHead>Order Value</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Delivery Name</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-gray-500 pt-12">
                      No claims, enjoy your day!
                    </TableCell>
                  </TableRow>
                ) : (
                  claims
                    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()) // Sort by latest
                    .slice(0, 10) // Show only 10 latest claims
                    .map((claim) => (
                      <TableRow
                        className="hover:cursor-pointer"
                        key={claim.order.orderId}
                        onClick={(e) => {
                          const directTo = `/orders/${claim.order.orderId}`;
                          // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
                          if (e.ctrlKey || e.metaKey) {
                            window.open(directTo, '_blank'); // Open in new tab
                          } else {
                            history.push(directTo); // Navigate in the same tab
                          }
                        }}
                      >
                        <TableCell className="font-medium">{claim.order.orderId}</TableCell>

                        <TableCell>{'€' + claim?.order.orderValue}</TableCell>
                        <TableCell>{formatLastUpdated(claim?.createdAt)}</TableCell>
                        <TableCell>{claim?.order.deliveryName || 'Unknown'}</TableCell>
                        {userRole === ROLES.ADMIN ? (
                          <TableCell>
                            <StatusMenu claim={claim} handleChange={handleClaimStatusChange} />
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Badge variant="outline">{formatClaimStatus(claim?.status)}</Badge>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>

            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  {currentPage !== 1 && (
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) setCurrentPage(currentPage - 1);
                      }}
                      className="text-black"
                    />
                  )}
                </PaginationItem>

                {Array.from({ length: metadata?.totalPages || 1 }, (_, index) => (
                  <PaginationItem key={index}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(index + 1);
                      }}
                      isActive={currentPage === index + 1}
                      className="text-black"
                    >
                      {index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem>
                  {currentPage !== metadata?.totalPages && (
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < metadata?.totalPages) setCurrentPage(currentPage + 1);
                      }}
                      className="text-black"
                    />
                  )}
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default Claims;

export const formatLastUpdated = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} hours ago`;
  return `${Math.floor(diffMinutes / 1440)} days ago`;
};

const formatClaimStatus = (status: string): string => {
  return status
    .toLowerCase()
    ?.split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

function StatusMenu({ claim, handleChange }) {
  return (
    <Menubar className="h-6 w-full">
      <MenubarMenu>
        <MenubarTrigger
          className="h-6 w-full whitespace-nowrap overflow-hidden text-ellipsis hover:cursor-pointer text-center" // Added text-center to center the text
          onClick={(e) => e.stopPropagation()}
        >
          {formatClaimStatus(claim.status)}
        </MenubarTrigger>
        <MenubarContent
          className="z-[1050] absolute bg-white shadow-md border rounded-md"
          style={{ position: 'absolute' }}
          onClick={(e) => e.stopPropagation()}
        >
          <MenubarItem
            onClick={(e) => {
              e.stopPropagation();
              handleChange(claim.id, 'FILED');
            }}
          >
            Filed
          </MenubarItem>
          <MenubarItem
            onClick={(e) => {
              e.stopPropagation();
              handleChange(claim.id, 'GRANTED');
            }}
          >
            Granted
          </MenubarItem>
          <MenubarItem
            onClick={(e) => {
              e.stopPropagation();
              handleChange(claim.id, 'REJECTED');
            }}
          >
            Rejected
          </MenubarItem>
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
}
